import { WorkflowGraph, WorkflowGraphNode } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { BRANCH_TYPES } from 'cio/models/action';

export type Point = {
  x: number;
  y: number;
};

export type EdgePathControlPoints = {
  points: Point[];
  endInArrow: boolean;
  sourceNode: WorkflowGraphNode;
  targetNode: WorkflowGraphNode;
  index?: number;
};

export type BaseEdgePathCommand = {
  command: 'M' | 'L' | 'Q';
  x: number;
  y: number;
  sourceNode: WorkflowGraphNode;
  targetNode: WorkflowGraphNode;
  pathIndex: number;
};
export type MoveEdgePathCommand = BaseEdgePathCommand & {
  command: 'M';
};
export type LineEdgePathCommand = BaseEdgePathCommand & {
  command: 'L';
  endInArrow?: boolean;
};
export type CurveEdgePathCommand = BaseEdgePathCommand & {
  command: 'Q';
  x1: number;
  y1: number;
};
export type EdgePathCommand = MoveEdgePathCommand | LineEdgePathCommand | CurveEdgePathCommand;

export type EdgePathSegment = [MoveEdgePathCommand, LineEdgePathCommand | CurveEdgePathCommand];

export const CURVE_RADIUS = 10;
const EXTRA_PATH_SECTION_LENGTH = 2;
const PATH_CONTROL_POINT_DISTANCE = 10;

export function convertPointsToPathSegments(controlPoints: EdgePathControlPoints): EdgePathSegment[] {
  // If there are only 2 points, this means we're only drawing one straight line
  if (controlPoints.points.length === 2) {
    let [startPoint, endPoint] = controlPoints.points;
    return [
      [
        {
          command: 'M',
          x: startPoint.x,
          y: startPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 0,
        },
        {
          command: 'L',
          x: endPoint.x,
          y: endPoint.y,
          endInArrow: controlPoints.endInArrow,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 1,
        },
      ],
    ];
  }

  // If there are 4 points, it means we're drawing a multi-stepped line. This
  // will require multiple paths:
  // a straight line down from the start node
  // a curve (to the left or right)
  // a horizontal line towards the target node
  // a curve downwards
  // a straight line to the target node
  if (controlPoints.points.length === 4) {
    let [startPoint, startControlPoint, endControlPoint, endPoint] = controlPoints.points;

    // This tells which direction the multi stepped line goes in. If it goes
    // left, the first curve's target x coordinate needs an -CURVE_RADIUS
    // adjustment, if it goes right, the target x coordinate needs +CURVE_RADIUS
    let firstCurveSign = Math.sign(endControlPoint.x - startControlPoint.x);

    return [
      [
        // Straight line down
        {
          command: 'M',
          x: startPoint.x,
          y: startPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 0,
        },
        {
          command: 'L',
          x: startControlPoint.x,
          y: startControlPoint.y - CURVE_RADIUS,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 1,
        },
      ],
      [
        // Curve left or right
        {
          command: 'M',
          x: startControlPoint.x,
          y: startControlPoint.y - CURVE_RADIUS,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 2,
        },
        {
          command: 'Q',
          x: startControlPoint.x + CURVE_RADIUS * firstCurveSign,
          y: startControlPoint.y,
          x1: startControlPoint.x,
          y1: startControlPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 3,
        },
      ],
      [
        // Straight horizontal line
        {
          command: 'M',
          x: startControlPoint.x + CURVE_RADIUS * firstCurveSign,
          y: startControlPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 4,
        },
        {
          command: 'L',
          x: endControlPoint.x - CURVE_RADIUS * firstCurveSign,
          y: endControlPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 5,
        },
      ],
      [
        // Curve down
        {
          command: 'M',
          x: endControlPoint.x - CURVE_RADIUS * firstCurveSign,
          y: endControlPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 6,
        },
        {
          command: 'Q',
          x: endControlPoint.x,
          y: endControlPoint.y + CURVE_RADIUS,
          x1: endControlPoint.x,
          y1: endControlPoint.y,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 7,
        },
      ],
      [
        // Final line down
        {
          command: 'M',
          x: endControlPoint.x,
          y: endControlPoint.y + CURVE_RADIUS,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 8,
        },
        {
          command: 'L',
          x: endPoint.x,
          y: endPoint.y,
          endInArrow: controlPoints.endInArrow,
          sourceNode: controlPoints.sourceNode,
          targetNode: controlPoints.targetNode,
          pathIndex: 9,
        },
      ],
    ];
  }

  // The previous steps in the paths functions only return sections of length 2
  // or 4, so at the moment we don't need to consider other cases
  return [];
}

export function getTargetNode(workflowGraph: WorkflowGraph, startNode: WorkflowGraphNode) {
  let outgoingEdges = workflowGraph.outEdges[startNode.id];

  if (outgoingEdges.length !== 1) {
    return null;
  }

  let [outgoingEdge] = outgoingEdges;
  return workflowGraph.nodes[outgoingEdge.to];
}

export function getNextNonDropNode(workflowGraph: WorkflowGraph, startNode: WorkflowGraphNode) {
  let targetNode = getTargetNode(workflowGraph, startNode);

  while (targetNode && targetNode.type === 'virtual_drop_area') {
    targetNode = getTargetNode(workflowGraph, targetNode);
  }

  return targetNode;
}

export function preparePathCoordinatesForBranchNodes(workflowGraph: WorkflowGraph) {
  let allBranchPaths: EdgePathControlPoints[] = [];

  let allNodes = Object.values(workflowGraph.nodes);
  let branchNodes = allNodes.filter((n) => BRANCH_TYPES.includes(n.type));

  branchNodes.forEach((branchNode) => {
    let outgoingEdgesCount = workflowGraph.outEdges[branchNode.id].length;

    if (outgoingEdgesCount === 1) {
      // If there's only one outgoing edge, we don't need any extra treatment
      // and can just draw a path like for any other regular action.
      // This can happen for a wait until action that only has a single (or
      // none) wait condition.
      let pathsForNode = prepareNonBranchPaths(branchNode, { workflowGraph });
      allBranchPaths = [...allBranchPaths, ...pathsForNode];
    } else {
      let pathsForNode = prepareBranchPaths(branchNode, { workflowGraph });
      allBranchPaths = [...allBranchPaths, ...pathsForNode];
    }
    let pathsForNode = prepareBranchPaths(branchNode, { workflowGraph });
    allBranchPaths = [...allBranchPaths, ...pathsForNode];
  });

  return allBranchPaths;
}

function prepareBranchPaths(branchNode: WorkflowGraphNode, { workflowGraph }: { workflowGraph: WorkflowGraph }) {
  let paths: EdgePathControlPoints[] = [];
  let branchPathStartPoint = {
    x: Math.floor(branchNode.x),
    y: Math.floor(branchNode.y - branchNode.height / 2),
  };
  let branchPathCurveOffsetPoint = {
    x: Math.floor(branchNode.x),
    y: Math.floor(branchNode.y + branchNode.height / 2 + PATH_CONTROL_POINT_DISTANCE),
  };

  let allNodes = Object.values(workflowGraph.nodes);
  let pathNodes = allNodes.filter((n) => n.type === 'virtual_path_label' && n.branchId === branchNode.id);

  pathNodes.forEach((pathNode, i) => {
    let pathLabelPoints: Point[] = [{ ...branchPathStartPoint }];

    // If the path label isn't centered underneath the branch node, we will
    // need to add additional control points to indicate where the curves
    // should be drawn for a multi-stepped line
    if (pathNode.x !== branchNode.x) {
      pathLabelPoints.push(
        { ...branchPathCurveOffsetPoint },
        {
          x: Math.floor(pathNode.x),
          y: Math.floor(branchNode.y + branchNode.height / 2 + PATH_CONTROL_POINT_DISTANCE),
        },
      );
    }

    pathLabelPoints.push({
      x: Math.floor(pathNode.x),
      y: Math.floor(pathNode.y + pathNode.height / 2),
    });

    paths.push({
      points: pathLabelPoints,
      endInArrow: false,
      sourceNode: branchNode,
      targetNode: pathNode,
      index: i,
    });
  });

  return paths;
}

export function preparePathCoordinatesForNonBranchNodes(workflowGraph: WorkflowGraph) {
  let allPaths: EdgePathControlPoints[] = [];

  let ignoreTypes = ['virtual_dummy', 'exit_action', 'virtual_drop_area', ...BRANCH_TYPES];
  let nodes = Object.values(workflowGraph.nodes).filter((n) => !ignoreTypes.includes(n.type));

  nodes.forEach((node) => {
    let paths = prepareNonBranchPaths(node, { workflowGraph });
    allPaths = [...allPaths, ...paths];
  });

  return allPaths;
}

function prepareNonBranchPaths(node: WorkflowGraphNode, { workflowGraph }: { workflowGraph: WorkflowGraph }) {
  let paths: EdgePathControlPoints[] = [];

  // If this is a rejoin node, check whether all incoming edges are virtual.
  // The edges would be "virtual" if they leave exits. Those edges exist
  // only for the layout algorithm, and should not be drawn.
  if (node.type === 'virtual_rejoin') {
    let incomingEdges = workflowGraph.inEdges[node.id];

    if (incomingEdges.every((e) => e.type === 'virtual')) {
      return paths;
    }
  }

  let target = getNextNonDropNode(workflowGraph, node);

  if (!target) {
    return paths;
  }

  let points: Point[] = [
    {
      x: Math.floor(node.x),
      y:
        node.type === 'virtual_path_label'
          ? Math.floor(node.y + node.height / 2 - EXTRA_PATH_SECTION_LENGTH)
          : Math.floor(node.y - node.height / 2),
    },
  ];

  // If the current node and it's target aren't vertically aligned, we need to
  // add extra control points for drawing the curved multi-line segments
  if (node.x !== target.x) {
    points.push(
      {
        x: Math.floor(node.x),
        y: Math.floor(target.y - target.height / 2 - PATH_CONTROL_POINT_DISTANCE),
      },
      {
        x: Math.floor(target.x),
        y: Math.floor(target.y - target.height / 2 - PATH_CONTROL_POINT_DISTANCE),
      },
    );
  }

  let endInArrow = true;
  if (target.type === 'virtual_rejoin') {
    points.push({
      x: Math.floor(target.x),
      y: Math.floor(target.y + target.height / 2),
    });
    endInArrow = false;
  } else {
    points.push({
      x: Math.floor(target.x),
      y: Math.floor(target.y - target.height / 2),
    });
  }

  paths.push({
    points,
    endInArrow,
    sourceNode: node,
    targetNode: target,
  });

  return paths;
}

// After creating and flattening path segments, we will end up with duplicate
// curves segments. For example, a multi split branch:
//            [ Branch ]
//                |
//  ----------------------------
//  |        |        |        |
//
//  Will have two curves facing left, and two curves facing right (as each path
//  edge is initially treated separately).
//  Similarly, rejoin points may have multiple curves going into them.
//  This function removes those duplicates so we don't draw overlapping curves.
export function removeDuplicateCurves(pathSegments: EdgePathSegment[]) {
  // We will use this hash to keep track of curves that have been processed to
  // check for duplicates. The keys of this array will be strings made from
  // concatenating start and end coordinates of the curves
  let processedCurves: Record<string, boolean> = {};

  let processedSegments: EdgePathSegment[] = [];

  pathSegments.forEach((segment) => {
    let [moveCommand, secondPathCommand] = segment;
    if (secondPathCommand.command === 'L') {
      processedSegments.push(segment);
      return;
    }

    let curveIdentifier = `${moveCommand.x}_${moveCommand.y}_${secondPathCommand.x}_${secondPathCommand.y}`;

    if (!processedCurves[curveIdentifier]) {
      processedCurves[curveIdentifier] = true;
      processedSegments.push(segment);
      return;
    }
  });

  return processedSegments;
}

// This function performs something similar to removeDuplicateCurves.
// In this multi split branch scenario:
//            [ Branch ]
//                |
//  ----------------------------
//  |        |        |        |
//
// There will be overlapping sections of the horizontal line, because each edge
// was added individually.
// This function first removes overlapping lines that start at the same
// coordinates, and then removes overlapping lines that end at the same
// coordinates. This will leave only the longest of each line intact.
export function removeOverlappingLines(pathSegments: EdgePathSegment[]) {
  let curves: EdgePathSegment[] = [];

  let lines: EdgePathSegment[] = [];

  let linesGroupedByStart: Record<string, EdgePathSegment[]> = {};

  pathSegments.forEach((segment) => {
    let [moveCommand, secondPathCommand] = segment;
    if (secondPathCommand.command === 'Q') {
      curves.push(segment);
      return;
    }

    let startIdentifier = `${moveCommand.x}_${moveCommand.y}`;

    if (!linesGroupedByStart[startIdentifier]) {
      linesGroupedByStart[startIdentifier] = [];
    }

    linesGroupedByStart[startIdentifier].push(segment);
  });

  lines = keepLongestLine(linesGroupedByStart);

  let linesGroupedByEnd: Record<string, EdgePathSegment[]> = {};

  lines.forEach((segment) => {
    let [, lineCommand] = segment;
    let endIdentifier = `${lineCommand.x}_${lineCommand.y}`;

    if (!linesGroupedByEnd[endIdentifier]) {
      linesGroupedByEnd[endIdentifier] = [];
    }

    linesGroupedByEnd[endIdentifier].push(segment);
  });

  lines = keepLongestLine(linesGroupedByEnd);

  return [...curves, ...lines];
}

export function keepLongestLine(groups: Record<string, EdgePathSegment[]>) {
  let processedSegments: EdgePathSegment[] = [];

  Object.values(groups).forEach((segments) => {
    if (segments.length === 1) {
      processedSegments.push(segments[0]);
      return;
    }

    let areLinesHorizontal = segments.every(
      (segment) => segment[0].y === segments[0][0].y && segment[1].y === segments[0][1].y,
    );

    let coord: 'x' | 'y' = areLinesHorizontal ? 'x' : 'y';

    let longestSegment = segments.slice(1).reduce((longestSegment, segment) => {
      let lengthOfLongestSegment = Math.abs(longestSegment[1][coord] - longestSegment[0][coord]);
      let lengthOfCurrentSegment = Math.abs(segment[1][coord] - segment[0][coord]);

      if (lengthOfCurrentSegment > lengthOfLongestSegment) {
        return segment;
      }

      return longestSegment;
    }, segments[0]);

    processedSegments.push(longestSegment);
  });

  return processedSegments;
}

export function convertPointsToWorkflowGraphPaths(paths: EdgePathControlPoints[]) {
  let allPathSegments: EdgePathSegment[] = [];
  paths.forEach((edgePath) => {
    allPathSegments.push(...convertPointsToPathSegments(edgePath));
  });

  let pathSegmentsWithoutDuplicateCurves = removeDuplicateCurves(allPathSegments);
  let pathSegmentsWithoutOverlappingLines = removeOverlappingLines(pathSegmentsWithoutDuplicateCurves);

  return pathSegmentsWithoutOverlappingLines.map((segment) => {
    let [moveCommand, secondPathCommand] = segment;

    // Add half pixels to help with antialiasing
    // https://stackoverflow.com/questions/23376308/avoiding-lines-between-adjecent-svg-rectangles/23376793#23376793
    let moveD = `M ${moveCommand.x + 0.5},${moveCommand.y + 0.5}`;
    let secondPathD = '';

    if (secondPathCommand.command === 'L') {
      secondPathD = `L ${secondPathCommand.x + 0.5},${secondPathCommand.y + 0.5}`;
    } else {
      secondPathD = `Q ${secondPathCommand.x1 + 0.5},${secondPathCommand.y1 + 0.5} ${secondPathCommand.x + 0.5},${
        secondPathCommand.y + 0.5
      }`;
    }

    return {
      id: `${secondPathCommand.command}_${moveCommand.sourceNode.id}_${moveCommand.targetNode.id}_${moveCommand.pathIndex}_${secondPathCommand.pathIndex}`,
      segment,
      d: `${moveD} ${secondPathD}`,
      endInArrow: secondPathCommand.command === 'L' ? secondPathCommand.endInArrow : false,
    };
  });
}

export function generatePaths(workflowGraph: WorkflowGraph) {
  let paths: EdgePathControlPoints[] = [
    ...preparePathCoordinatesForBranchNodes(workflowGraph),
    ...preparePathCoordinatesForNonBranchNodes(workflowGraph),
  ];

  workflowGraph.paths = convertPointsToWorkflowGraphPaths(paths);
}
